export type TeamInfo = {
  created_at: string,
  deleted_at: string,
  id: string,
  name: string,
  personal_team: boolean,
  updated_at: string,
  user_id: string,
}

export type UserInfo = {
  admin: boolean
  brevo_id?: string
  brevo_sync_error?: string
  created_at: Date
  current_team_id: string
  deleted_at?: Date
  email: string
  email_verified_at: Date
  first_name: string
  hasTeams: boolean
  id: string
  is_admin: boolean
  last_name: string
  name: string
  owned_teams: TeamInfo[]
  profile_photo_path?: string
  profile_photo_url?: string
  registration_comment?: string
  registration_redirect?: string
  super_admin: boolean
  teams: TeamInfo[]
  title: string
  token_id?: string
  two_factor_confirmed_at?: Date
  updated_at: string
}

const emptyData = {
  admin: false,
  created_at: new Date(),
  current_team_id: '',
  email: '',
  email_verified_at: new Date(),
  first_name: '',
  hasTeams: false,
  id: '',
  is_admin: false,
  last_name: '',
  name: '',
  owned_teams: [],
  super_admin: false,
  teams: [],
  title: '',
  updated_at: ''
}

const storedUserInfo = getCookie('user_info')
const hasEnoughSessionData = getCookie('access_token') && storedUserInfo
const userInfo = reactive<UserInfo>(hasEnoughSessionData ? JSON.parse(storedUserInfo) : { ...emptyData })
const loggedIn = ref(!!hasEnoughSessionData)
const config = reactive({ features: [''] })

export const useUserInfo = () => {
  const { public: { authHost } } = useRuntimeConfig()

  const update = (jsonData: Record<string, any>) => {
    setCookie('user_info', JSON.stringify(jsonData), 1)
    userInfo.admin = jsonData.admin
    userInfo.brevo_id = jsonData.brevo_id
    userInfo.brevo_sync_error = jsonData.brevo_sync_error
    userInfo.created_at = new Date(jsonData.created_at)
    userInfo.current_team_id = jsonData.current_team_id
    userInfo.deleted_at = jsonData.deleted_at ? new Date(jsonData.deleted_at) : undefined
    userInfo.email = jsonData.email
    userInfo.email_verified_at = new Date(jsonData.email_verified_at)
    userInfo.first_name = jsonData.first_name
    userInfo.hasTeams = jsonData.hasTeams
    userInfo.id = jsonData.id
    userInfo.is_admin = jsonData.is_admin
    userInfo.last_name = jsonData.last_name
    userInfo.name = jsonData.name
    userInfo.owned_teams = jsonData.owned_teams
    userInfo.profile_photo_path = jsonData.profile_photo_path
    userInfo.profile_photo_url = jsonData.profile_photo_url
    userInfo.registration_comment = jsonData.registration_comment
    userInfo.registration_redirect = jsonData.registration_redirect
    userInfo.super_admin = jsonData.super_admin
    userInfo.teams = jsonData.teams
    userInfo.title = jsonData.title
    userInfo.token_id = jsonData.token_id
    userInfo.two_factor_confirmed_at = jsonData.two_factor_confirmed_at ? new Date(jsonData.two_factor_confirmed_at) : undefined
    userInfo.updated_at = jsonData.updated_at
  }

  const updateFeatures = async () => {
    if ([null, 'invalid'].includes(getCookie('lexsearch_token'))) {
      await fetchLexSearchToken()
    }
    if (getCookie('lexsearch_token') !== 'invalid') {
      config.features.push('search')
    }
  }

  const fetchUserInfo = async () => {
    if (loggedIn.value) {
      await updateFeatures()
      return
    }
    const accessToken = getCookie('access_token')
    if (accessToken !== null) {
      const req = await fetch(`${authHost}/api/user`, { redirect: 'manual', headers: { Authorization: `Bearer ${accessToken}` } })
      if (!req.redirected && req.status === 200) {
        update(await req.json())
        loggedIn.value = true
        await updateFeatures()
      }
    } else {
      clearSession()
    }
  }
  // eslint-disable-next-line require-await
  const clearSession = async (keepLoggedin: Boolean = false) => {
    // keepLoggedin is only set to true to prevent immediate trigger of rerendering during logout redirect
    if (!keepLoggedin) {
      Object.assign(userInfo, emptyData)
      loggedIn.value = false
    }
    clearCookie('user_info')
    clearCookie('access_token')
    clearCookie('refresh_token')
    clearCookie('lexsearch_token')
  }

  return {
    loggedIn,
    userInfo,
    config,
    update,
    fetchUserInfo,
    clearSession
  }
}
